'use client';

import Image from 'next/image';
import React from 'react';
import { LocalizedLink } from '@/components/LocalizedLink';
import { useTranslations } from '@/hooks/useTranslations';
import type { GameProviderEntry } from '@/lib/cms';
import { getContentImageMeta } from '@/lib/image-meta';
import { getProviderLogoUrl } from '@/lib/provider-image';

function getLogoUrl(provider: GameProviderEntry): string | null {
  const logo = provider.logo;
  if (logo && typeof logo === 'object') {
    const data = logo as { data?: { attributes?: { url?: string } }; url?: string };
    const url = data.url ?? data.data?.attributes?.url;
    if (typeof url === 'string' && url) return url;
  }
  const slug = provider.slug?.trim();
  if (slug) return getProviderLogoUrl(slug);
  return null;
}

type Props = {
  providers: GameProviderEntry[];
  cmsBase: string;
};

const SCROLL_AMOUNT = 300;

export function ProvidersCarousel({ providers, cmsBase }: Props) {
  const { t } = useTranslations();
  const scrollRef = React.useRef<HTMLDivElement>(null);

  const scroll = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const delta = direction === 'left' ? -SCROLL_AMOUNT : SCROLL_AMOUNT;
    el.scrollBy({ left: delta, behavior: 'smooth' });
  };

  return (
    <div className="relative -mx-6 overflow-hidden px-6">
      <button
        type="button"
        onClick={() => scroll('left')}
        aria-label={t('home.providersScrollLeft')}
        className="absolute left-2 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-[#1e2235]/90 text-white shadow-lg backdrop-blur-sm transition hover:border-[#ffb74d]/50 hover:bg-[#ffb74d]/10"
      >
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      <button
        type="button"
        onClick={() => scroll('right')}
        aria-label={t('home.providersScrollRight')}
        className="absolute right-2 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-[#1e2235]/90 text-white shadow-lg backdrop-blur-sm transition hover:border-[#ffb74d]/50 hover:bg-[#ffb74d]/10"
      >
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
      <div
        ref={scrollRef}
        className="scrollbar-hide flex gap-4 overflow-x-auto pb-4 pt-2"
      >
        {providers.map((provider, index) => {
          const logoUrl = getLogoUrl(provider);
          const slug = provider.slug ?? '';
          const name = provider.name ?? 'Unknown';
          const href = slug ? `/game-providers/${encodeURIComponent(slug)}` : '/game-providers';
          const imageMeta = getContentImageMeta({
            pageTitle: t('nav.gameProviders'),
            imageNumber: index + 1,
          });

          return (
            <LocalizedLink
              key={provider.documentId ?? provider.id ?? provider.slug ?? index}
              href={href}
              className="group flex h-44 w-48 shrink-0 flex-col items-center justify-center gap-2 rounded-xl border border-white/10 bg-[#1e2235]/50 p-4 transition-all hover:border-[#ffb74d]/40 hover:bg-[#1e2235]"
            >
              {logoUrl ? (
                <Image
                  src={
                    logoUrl.startsWith('http') || logoUrl.startsWith('/')
                      ? logoUrl
                      : `${cmsBase}${logoUrl}`
                  }
                  alt={imageMeta.alt}
                  title={name}
                  width={112}
                  height={112}
                  sizes="112px"
                  className="h-28 w-28 object-contain transition-transform group-hover:scale-110"
                />
              ) : (
                <span className="flex h-28 w-28 items-center justify-center text-2xl font-bold text-slate-600">
                  {name.slice(0, 2).toUpperCase()}
                </span>
              )}
              <span className="line-clamp-2 text-center text-xs font-medium text-white group-hover:text-[#ffb74d]">
                {name}
              </span>
            </LocalizedLink>
          );
        })}
      </div>
    </div>
  );
}
